<template>
  <div>
    <app-header title="">
      <template v-slot:actions>
        <c-button @click="update" class="btn btn-primary mt-2 mt-xl-0">
          <i class="far fa-sync"></i> Run Panda Sync
        </c-button>
      </template>
    </app-header>

    <div class="container my-5">
      <div class="row mb-4">
        <div class="col d-flex align-items-center">
          <h2 class="mb-0">Sales Dashboard</h2>
        </div>
        <div class="col-12 col-md-4 col-xl-3">
          <multiselect
            v-model="financialTarget"
            :options="financialTargets"
            label="financial_year"
            track-by="id"
            placeholder="Select Year"
            :show-labels="false"
            :allow-empty="false"
            :searchable="false"
            @select="getData($event.id)"
          ></multiselect>
        </div>
      </div>

      <div v-if="loading" class="py-5 text-center">
        <span class="spinner-border text-primary"></span>
      </div>

      <template v-else>
        <div class="row">
          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">Sales <span class="badge rounded-pill bg-primary float-end">Annual</span></h3>
                <div class="row">
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Current</p>
                    <p class="lead mb-0">${{ data.annual_revenue | currency0 }}</p>
                  </div>
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Target</p>
                    <p class="lead mb-0">${{ data.annual_target | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Remaining</p>
                    <p class="lead mb-0">${{ data.annual_target - data.annual_revenue | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Position</p>
                    <p class="lead mb-0" :class="data.position >= 0 ? 'text-success' : 'text-danger'">
                      {{ data.position >= 0 ? '+' : '-' }} ${{ Math.abs(data.position) | currency0 }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">Sales <span class="badge rounded-pill bg-info float-end">Month</span></h3>
                <div class="row">
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Current</p>
                    <p class="lead mb-0">${{ data.month_sales | currency0 }}</p>
                  </div>
                  <div class="col-6 mb-3">
                    <p class="small text-muted mb-1">Target</p>
                    <p class="lead mb-0">${{ data.monthly_target | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Remaining</p>
                    <p class="lead mb-0" :class="monthlyRemaining > 0 ? 'text-danger' : 'text-success'">
                      ${{ Math.max(monthlyRemaining, 0) | currency0 }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-xl-4 mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">PI Sales <span class="badge rounded-pill bg-info float-end">Monthly</span></h3>
                <div class="row">
                  <div class="col-6">
                    <p class="small text-muted mb-1">Total</p>
                    <p class="lead mb-0">${{ data.pi_recurring | currency0 }}</p>
                  </div>
                  <div class="col-6">
                    <p class="small text-muted mb-1">Cluster (hosting)</p>
                    <p class="lead mb-0">${{ data.pi_cluster_recurring | currency0 }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 class="mb-3">Conversions</h3>
        <div class="row">
          <div v-for="conversion in conversions" :key="conversion.title" class="col-md-6 col-xl mb-4">
            <div class="card card-widget h-100">
              <div class="card-body">
                <h3 class="widget-title">{{ conversion.title }}</h3>
                <p class="lead mb-1">
                  {{ conversion.signed }} <small class="text-muted">signed of {{ conversion.sent }} sent</small>
                </p>
                <div class="progress mb-1">
                  <div class="progress-bar" :style="{ width: conversion.percentage + '%' }"></div>
                </div>
                <p class="small text-muted mb-0">{{ conversion.percentage }}% conversion</p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xl-6 mb-4">
            <div class="card h-100">
              <div class="card-body">
                <h3 class="card-title">Monthly Quotes</h3>
                <columns-chart :columns="data.monthly" type="bar" :height="460" :targets="monthlyTargets" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card mb-4">
              <div class="card-body">
                <h3 class="card-title">Leaderboard</h3>
                <columns-chart
                  :columns="data.leaderboard"
                  type="bar"
                  :height="220"
                  stacked
                  horizontal
                  :colors="leaderboardColors"
                />
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-body">
                <h3 class="card-title">Win Loss</h3>
                <donut-chart :segments="winloss" :colors="['#63D2BC', '#F52441', '#6F98D5']" :height="220" />
              </div>
            </div>
          </div>
        </div>

        <h3 class="mb-3">Waiting Quotes</h3>
        <c-data-list :items="data.waiting || []" key-by="uuid" class="no-links">
          <template v-slot:columns>
            <div class="col-12 col-lg-4">
              <p>Company</p>
            </div>
            <div class="col-12 col-lg-4">
              <p>Name</p>
            </div>
            <div class="col-6 col-lg-2">
              <p>Status</p>
            </div>
            <div class="col-6 col-lg-2 text-lg-end">
              <p>Total</p>
            </div>
          </template>
          <template v-slot:default="{ item }">
            <div class="row">
              <div class="col-12 col-lg-4">
                <p class="strong">{{ item.company_name }}</p>
              </div>
              <div class="col-12 col-lg-4">
                <p>{{ item.name }}</p>
              </div>
              <div class="col-6 col-lg-2">
                <p>
                  <span class="data-label d-lg-none">Status:</span>
                  {{ item.status }}
                </p>
              </div>
              <div class="col-6 col-lg-2 justify-content-lg-end">
                <p class="text-lg-end">
                  <span class="data-label d-lg-none">Total:</span>
                  ${{ item.total_ex | currency }}
                </p>
              </div>
            </div>
          </template>
          <template v-slot:empty>
            <li class="py-4">
              <p class="text-center">No quotes waiting.</p>
            </li>
          </template>
        </c-data-list>
      </template>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { notify, http } from 'utils'
import ColumnsChart from './charts/ColumnsChart'
import DonutChart from './charts/DonutChart'

export default {
  components: {
    Multiselect,
    ColumnsChart,
    DonutChart
  },

  data () {
    return {
      loading: true,
      data: {},
      financialTarget: null,
      financialTargets: [],
      leaderboardColors: {
        Accepted: '#63D2BC',
        'Awaiting acceptance': '#6F98D5',
        Draft: '#FFCD79',
        Withdrawn: '#c5cae0',
        Expired: '#F52441'
      }
    }
  },

  computed: {
    monthlyRemaining () {
      return this.data.monthly_target - this.data.month_sales
    },

    monthlyTargets () {
      return this.data.monthly_target > 0
        ? [{ value: this.data.monthly_target, label: 'Sales Target', color: '#2ba97a' }]
        : []
    },

    winloss () {
      return (this.data.winloss || []).map(([label, value]) => ({ label, value }))
    },

    conversions () {
      return [
        { title: 'Stage 1', key: 's1' },
        { title: 'Stage 2', key: 's2' },
        { title: 'Stage 1 & 2', key: 's12' },
        { title: 'Stage 3', key: 's3' },
        { title: 'Full Budget', key: 'full' }
      ].map(({ title, key }) => {
        const sent = this.data[`${key}_sent`] || 0
        const signed = this.data[`${key}_signed`] || 0

        return {
          title,
          sent,
          signed,
          percentage: sent > 0 ? Math.min(Math.round(signed / sent * 100), 100) : 0
        }
      })
    }
  },

  created () {
    // The sales endpoint doesn't return its financial target, so select the current one
    http.get('/financial-targets').then((response) => {
      this.financialTargets = response.data
      this.financialTarget = this.financialTargets.find(({ financial_year_start, financial_year_end }) => {
        return moment().isBetween(financial_year_start, financial_year_end, null, '[]')
      }) || null
    })

    this.getData()
  },

  methods: {
    getData (financialTargetId = null) {
      this.loading = true

      const query = financialTargetId ? `?financial-target=${financialTargetId}` : ''

      return http.get('/dashboards/sales' + query).then((response) => {
        this.data = response.data
      }).finally(() => {
        this.loading = false
      })
    },

    update () {
      return http.put('/dashboards/sales', {}).then(() => {
        notify.success('Sync Running, refresh in a minute to see the changes')
      }).catch(() => {
        notify.error('Failed to start the Panda sync, please try again.')
      })
    }
  }
}
</script>
